import type { AxiosInstance } from 'axios';
import type { ApiPaginationResponse, ApiResponse } from '@/types';
import type {
  BettingSummaryQuery,
  BettingSummaryResponse,
  GetOperationHistoryQuery,
  GetOperationHistoryResponse,
  KycHistoryResponse,
  MemberBalanceInfo,
  MemberDetailInfo,
  RemarkHistoryResponse,
  UpdateMemberInfoQuery, UpdateMemberPipQuery,
  UpdateMemberRemarkQuery,
  MemberSettingResponse,
  MemberSettingUserGroupQuery,
  MemberSettingStateQuery,
  MemberSettingPWLockStateQuery,
  GetBonusHistoryQuery,
  GetBonusHistoryResponse,
  SetSelfExclusionQuery,
  MemberSettingQuery,
  GetVipBonusRecordsResponse,
  RequestMemberInfoVipQuery,
  GetVipLevelRecordsResponse,
  GetVipMonthlyTurnoverResponse
} from '@/types/api/memberInfo.type';

import type { GetIdentifyReponse } from '@/types/api/identify.type';

export default function MemberApi(ai: AxiosInstance) {
  // 获取用户详情
  function getMemberDetailInfo<D extends MemberDetailInfo, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/detail', { params: { id } });
  }

  //获取balance信息
  function getMemberBalanceInfo<D extends MemberBalanceInfo, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/balance', { params: { id } });
  }

  //获取remark 历史信息
  function getRemarkHistory<D extends RemarkHistoryResponse, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/remark/history', { params: { id } });
  }

  //获取kyc 历史信息
  function getKycHistory<D extends KycHistoryResponse, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/kycHistory', { params: { id } });
  }

  //userinfo 获取kyc
  function getUserKycInfo<D extends GetIdentifyReponse, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/kycHistoryList', { params: { id } });
  }

  //获取bettingSummary
  function getBettingSummary<D extends BettingSummaryResponse, R extends ApiResponse<D>>(params: BettingSummaryQuery) {
    return ai.get<R>('/api/v2/member/bettingSummary', { params });
  }

  //获取setting
  function getSettingInfo<D extends MemberSettingResponse, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/setting', { params: { id } });
  }

  // setting修改;
  function updateSettingInfo<D extends MemberSettingResponse, R extends ApiResponse<D>>(params: MemberSettingQuery) {
    return ai.patch<R>('/api/v2/member/setting', params);
  }

  // 用户锁定状态修改
  function updatePWLockState<D extends Record<string, never>, R extends ApiResponse<D>>(params: MemberSettingPWLockStateQuery) {
    return ai.post<R>('/api/v2/member/updatePWLockState', params);
  }

  // 用户状态修改
  function updateSettingState<D extends Record<string, never>, R extends ApiResponse<D>>(params: MemberSettingStateQuery) {
    return ai.post<R>('/api/v2/member/updateState', params);
  }

  // 用户组修改
  function updateSettingUserGroup<D extends Record<string, never>, R extends ApiResponse<D>>(params: MemberSettingUserGroupQuery) {
    return ai.post<R>('/api/v2/member/updateGroup', params);
  }

  //重置密码
  function resetPassword<D extends Record<string, never>, R extends ApiResponse<D>>(id: string) {
    return ai.post<R>('/api/v2/member/resetPassword', { id });
  }

  //获取operation history信息
  function getOperationHistory<D extends GetOperationHistoryResponse, R extends ApiResponse<D>>(params: GetOperationHistoryQuery) {
    return ai.get<R>('/api/v2/member/operationHistory', { params });
  }

  //获取bonus history信息
  function getBonusHistory<D extends GetBonusHistoryResponse, R extends ApiPaginationResponse<D>>(params: GetBonusHistoryQuery) {
    return ai.get<R>('/api/v2/member/bonusHistory', { params });
  }

  //更新用户详情
  function updateMemberInfo<D extends Record<string, never>, R extends ApiResponse<D>>(params: UpdateMemberInfoQuery) {
    return ai.patch<R>('/api/v2/member/detail', params);
  }

  //解绑idNumber(cpf)
  function unbindCpf<D extends Record<string, never>, R extends ApiResponse<D>>(memberId: string) {
    return ai.patch<R>('/api/v2/member/unbindCpf', { memberId });
  }

  //更新用户备注信息
  function updateMemberRemark<D extends Record<string, never>, R extends ApiResponse<D>>(params: UpdateMemberRemarkQuery) {
    return ai.put<R>('/api/v2/member/remark', params);
  }

  // 修改用户kyc状态
  function updateKycStatus<D extends Record<string, never>, R extends ApiResponse<D>>(id: string) {
    return ai.post<R>('/api/v2/member/updateKycStatus', { id });
  }

  // 修改用户PIP状态
  function updatePipStatus<D extends Record<string, never>, R extends ApiResponse<D>>(param: UpdateMemberPipQuery) {
    return ai.post<R>('/api/v2/member/updatePipStatus', param);
  }

  // 修改用户excluded状态
  function setSelfExclusion<D extends Record<string, never>, R extends ApiResponse<D>>(param: SetSelfExclusionQuery) {
    return ai.post<R>('/api/v2/member/setSelfExclusion', param);
  }

  //获取pip reference信息
  function getPipReferenceInfo<D extends Record<string, string>, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/getPipReference', { params: { memberId: id } });
  }

  //获取Vip Bonus Records信息
  function getVipBonusRecords<D extends GetVipBonusRecordsResponse, R extends ApiPaginationResponse<D>>(params: RequestMemberInfoVipQuery) {
    return ai.get<R>('/api/v2/vip/bonusRecords', { params });
  }

  //获取Vip Bonus Records信息
  function getVipLevelRecords<D extends GetVipLevelRecordsResponse, R extends ApiPaginationResponse<D>>(params: RequestMemberInfoVipQuery) {
    return ai.get<R>('/api/v2/vip/levelRecords', { params });
  }

  //获取Vip Bonus Records信息
  function getVipMonthlyTurnover<D extends GetVipMonthlyTurnoverResponse, R extends ApiPaginationResponse<D>>(params: RequestMemberInfoVipQuery) {
    return ai.get<R>('/api/v2/vip/monthlyTurnover', { params });
  }

  return {
    getMemberDetailInfo,
    getMemberBalanceInfo,
    getBettingSummary,
    getSettingInfo,
    getRemarkHistory,
    resetPassword,
    getBonusHistory,
    getOperationHistory,
    updateMemberInfo,
    unbindCpf,
    updateMemberRemark,
    getKycHistory,
    getUserKycInfo,
    updateKycStatus,
    updatePipStatus,
    updateSettingUserGroup,
    updateSettingState,
    updatePWLockState,
    setSelfExclusion,
    getPipReferenceInfo,
    updateSettingInfo,
    getVipBonusRecords,
    getVipLevelRecords,
    getVipMonthlyTurnover
  };
}
